import Router from 'next/router'
import { useEffect, useRef } from 'react'

import { getUnlockedScrollY } from '@src/quince-components/src/atoms/modal/hooks/useLockBodyScroll'
import {
  isBackNavRestoreClaimed,
  releaseClaimsFromPreviousPageLoad,
  writeNextScrollSnapshot,
} from '@src/utils/back-nav-scroll-ownership'

// Keep Next's own back-navigation scroll snapshot honest while a modal holds the body lock.
//
// Next owns scroll restoration here (next.config `experimental.scrollRestoration`): inside
// `router.push()` it snapshots `window.scrollY` into `sessionStorage['__next_scroll_' + key]` for the
// entry being left. A body-locking modal (Quick Add) pins <body> with `position: fixed`, so that read
// returns 0 — and Back lands at the top of the page we left. Measured on pre-integ: leave Home at
// y=720 via Quick Add → "view full product details", snapshot written as `{"x":0,"y":0}`, Back → 0.
//
// Pages with their own scroll cache (PDP, collection, search, PL) already record the true position
// through `getUnlockedScrollY`. Home has none and relies entirely on Next, so correct the value Next
// already stores rather than building a second save/restore path for it.
//
// Verified against next 15.5.18: the snapshot is written in `Router.push` (router.js:462) BEFORE
// `routeChangeStart` is emitted (router.js:844), and before the new entry's key is minted in
// `changeState` (router.js:1096) — so a `routeChangeStart` listener overwrites the departing entry's
// snapshot after Next has written it. [STRFRNT-15288]
//
// ponytail: piggy-backs on Next's restoration instead of adding a Home-specific store. Ceiling —
// it depends on Next's private `__next_scroll_<key>` sessionStorage format; a Next upgrade that
// renames it makes this hook a silent no-op, which is what the unit test guards.

const getHistoryKey = (): string | null => {
  const state = window.history.state as { key?: unknown } | null
  return typeof state?.key === 'string' ? state.key : null
}

// Subscribe through the `next/router` singleton, not `useRouter()`: this hook mounts in `_app`, and
// `useRouter()` throws "NextRouter was not mounted" whenever there is no RouterContext above it (it
// broke every `_app.page.test.tsx` case). Next documents `events` as a static that works without an
// initialised router ("the router doesn't have to be initialized to use it" — client/router.js:89).
export const useScrollSnapshotUnderLock = () => {
  // The entry we are leaving. Tracked as we go rather than read inside the handler, because on a
  // back/forward navigation Next advances `history.state` to the DESTINATION before it emits
  // `routeChangeStart` (router.js:1685) — reading it there would rewrite the wrong entry.
  const departingKeyRef = useRef<string | null>(null)

  useEffect(() => {
    departingKeyRef.current = getHistoryKey()

    // Same job as the handler below — keep Next's snapshot honest — for the one case a handler cannot
    // reach: entries a PDP claimed before this page's JavaScript existed. Their anchors died with the
    // previous page, so Next has to be given its pixel back or Back lands at the top on an entry it
    // used to restore correctly. Runs before any popstate can, since the reader has to press Back.
    releaseClaimsFromPreviousPageLoad()

    const rememberEntry = () => {
      departingKeyRef.current = getHistoryKey()
    }

    const correctSnapshot = () => {
      const scrollY = getUnlockedScrollY()
      // Equal whenever no modal holds the lock, so Next's own snapshot is already correct and this
      // hook stays inert on every ordinary navigation.
      if (scrollY === window.scrollY) return

      const key = departingKeyRef.current
      if (!key) return

      // A page with its own height-aware restore has already taken this entry over, and handing Next a
      // real pixel would put it back in the race: on a deep PDP it lands on a document that has not
      // finished growing, which is the footer. Leave the claim alone. [STRFRNT-15288]
      if (isBackNavRestoreClaimed(key)) return

      writeNextScrollSnapshot(key, { x: window.scrollX, y: scrollY })
    }

    Router.events.on('routeChangeStart', correctSnapshot)
    Router.events.on('routeChangeComplete', rememberEntry)

    return () => {
      Router.events.off('routeChangeStart', correctSnapshot)
      Router.events.off('routeChangeComplete', rememberEntry)
    }
  }, [])
}
